import type { TrackerCalcDef, TrackerKpi, TrackerValue } from 'chery-contracts';
import {
  buildRangeSummaryResolver,
  buildValueResolver,
  type CalcKpiDef,
  type FormulaNode,
  type RangeSummary,
  type ValueResolver,
} from 'chery-kpi-engine';
import { parseCellInput } from './tracker-display';
import { cellKey, type CellValue } from './tracker-state';

/**
 * The browser side of compute-on-read (plan §3.1, §7): every number the grid shows — a calculated cell, the
 * Total / Average / Latest of any row, the CSV — is read through one resolver built here with the shared engine,
 * over the saved cells, the staged edits **and the text still being typed** in the open cell editor. So a keystroke
 * in `SELL-SOLD` moves `SELL-CONVERSION` before Save, with exactly the arithmetic the server would use.
 */

/** The value being typed in the open editor, already read with the KPI's unit; `null` is a blank. */
export interface TrackerDraft {
  kpiId: string;
  weekId: string;
  value: CellValue;
}

/**
 * What the editor holds, as a draft value — or `null` (no draft) when the text cannot be read yet (`12a`), so the
 * cell's staged value keeps feeding the formulas until the text makes sense again.
 */
export const draftFromInput = (kpi: Pick<TrackerKpi, 'id' | 'unit'>, weekId: string, raw: string): TrackerDraft | null => {
  const parsed = parseCellInput(kpi.unit, raw);

  return parsed.invalid === undefined ? { kpiId: kpi.id, weekId, value: parsed.value ?? null } : null;
};

/**
 * The engine's definitions for the grid: every shown KPI (manual or calculated, restricted flagged), plus the
 * calculated KPIs the shown formulas read without being shown themselves. Shown KPIs are active by construction;
 * an inactive source simply has no definition and no values, so it resolves blank, as on the server.
 */
export const trackerCalcDefs = (
  kpis: readonly Pick<TrackerKpi, 'id' | 'source' | 'restricted'>[],
  calcDefs: readonly TrackerCalcDef[],
): CalcKpiDef[] => {
  const formulas = new Map(calcDefs.map((def) => [def.kpiId, def.formula as FormulaNode]));
  const shown = new Set(kpis.map((kpi) => kpi.id));

  return [
    ...kpis.map((kpi): CalcKpiDef => ({
      id: kpi.id,
      source: kpi.source,
      active: true,
      restricted: kpi.restricted,
      formula: kpi.source === 'CALCULATED' && !kpi.restricted ? (formulas.get(kpi.id) ?? null) : null,
    })),
    ...calcDefs
      .filter((def) => !shown.has(def.kpiId))
      .map((def): CalcKpiDef => ({ id: def.kpiId, source: 'CALCULATED', active: true, formula: def.formula as FormulaNode })),
  ];
};

export interface TrackerValueSources {
  defs: readonly CalcKpiDef[];
  /** A shown KPI's saved cell with its staged edit on top (Text KPIs answer their text). */
  shownValueAt: (kpiId: string, weekId: string) => CellValue;
  /** Whether `kpiId` is a shown KPI (otherwise it is a source read from the stored cells only). */
  isShown: (kpiId: string) => boolean;
  /** The grid's stored cells, which include the manual sources of the formulas. */
  stored: Readonly<Record<string, TrackerValue>>;
  draft: TrackerDraft | null;
}

/** `(kpiId, weekId) → value` for every KPI on screen and every source behind them, through the engine. */
export const buildTrackerValueAt = ({ defs, shownValueAt, isShown, stored, draft }: TrackerValueSources): ValueResolver =>
  buildValueResolver(defs, (kpiId, weekId) => {
    if (draft && draft.kpiId === kpiId && draft.weekId === weekId) {
      return draft.value;
    }

    return isShown(kpiId) ? shownValueAt(kpiId, weekId) : (stored[cellKey(kpiId, weekId)]?.value ?? null);
  });

/** Total / Average / Latest per KPI over the range's weeks, from the same resolver the cells use. */
export const buildTrackerSummaryOf = (weekIds: readonly string[], valueAt: ValueResolver): ((kpiId: string) => RangeSummary) =>
  buildRangeSummaryResolver(weekIds, valueAt);
